// Dashboard.jsx
import React, {useState, useMemo, useEffect} from "react";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import WeekdayRevenueChart from "../../components/WeekdayRevenueChart/WeekdayRevenueChart";

import MapView from "./components/Map";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from "@mui/material";

import { subDays } from 'date-fns';

import { ResponsiveBar } from "@nivo/bar";
import CircularProgress from "@mui/material/CircularProgress";
import AddCardModal from './components/AddCardModal';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { getCurrentUser } from 'aws-amplify/auth';
import {useMaterialUIController} from "../../context";
import Icon from "@mui/material/Icon";

// helpers (כמו שכבר היו)
function formatYMD(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function calcMoM(curr, prev) {
    if (prev == null || prev === 0) return null;
    return ((curr - prev) / Math.abs(prev)) * 100;
}
function formatPct(pct) {
    if (pct == null || Number.isNaN(pct)) return "—";
    const sign = pct >= 0 ? "+" : "";
    return `${sign}${pct.toFixed(1)}%`;
}
function trendColor(pct) {
    if (pct == null || Number.isNaN(pct)) return "secondary";
    return pct >= 0 ? "success" : "error";
}

const yesterdayKey = formatYMD(subDays(new Date(), 1));
const lastWeekKey  = formatYMD(subDays(new Date(), 8));
const today = new Date();

export default function Dashboard() {
    const [stats,setStats] = useState([
        { color: "dark", icon: "₪", title: "Total", count: 0 }
    ]);
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [barData, setBarData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openIdx, setOpenIdx] = useState(-1);

    const [userCards, setUserCards] = useState([]);
    const [cardStats, setCardStats] = useState({});

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const [userEmail, setUserEmail] = useState("");

    const formatCurrency = (value) =>
        value != null
            ? value.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            })
            : '';

    // get current user email
    useEffect(() => {
        (async function getUserData() {
            const data = await getCurrentUser();
            setUserEmail(data.signInDetails.loginId);
        })();
    }, []);

    // fetch cards
    const reloadCards = async () => {
        const res = await fetch('https://blue-parking-invite.bluept.dev/api/cards');
        const cards = await res.json();
        setUserCards(cards);
    };

    useEffect(() => {
        reloadCards().catch(console.error);
    }, []);

    // add new card
    const handleNewCard = async (card) => {
        await fetch('https://blue-parking-invite.bluept.dev/api/cards', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(card),
        });
        await reloadCards();
        setAddModalOpen(false);
    };

    // save edit
    const handleEditSave = async (updated) => {
        if (!editingCard) return;
        await fetch(
            `https://blue-parking-invite.bluept.dev/api/cards/${editingCard.id}`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updated),
            }
        );
        await reloadCards();
        setEditModalOpen(false);
        setEditingCard(null);
    };

    // delete from modal
    const handleEditDelete = async () => {
        if (!editingCard) return;
        await fetch(
            `https://blue-parking-invite.bluept.dev/api/cards/${editingCard.id}`,
            { method: 'DELETE' }
        );
        await reloadCards();
        setEditModalOpen(false);
        setEditingCard(null);
    };

    // toggle edit mode
    const toggleEditMode = () => {
        setIsEditMode((prev) => !prev);
        setOpenIdx(-1);           // שלא יישאר גרף פתוח
        setEditingCard(null);
        setEditModalOpen(false);
    };

    // fetch stats per place when userCards change (כמו שכבר עשית)
    useEffect(() => {
        if (!userCards.length) return;

        const payload = {
            placeIds: userCards.map(c => c.place_id)
        };

        fetch('https://blue-parking-invite.bluept.dev/api/daily-stats/batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then(res => {
                if (!res.ok) throw new Error(res.statusText);
                return res.json();
            })
            .then(({ results }) => {
                const statsMap = results.reduce((acc, { placeId, data }) => {
                    const graph = data.data || [];

                    const yObj  = graph.find(d => d.x === yesterdayKey);
                    const lwObj = graph.find(d => d.x === lastWeekKey);

                    const yRev  = yObj  ? yObj["Current Month"] : 0;
                    const lwRev = lwObj ? lwObj["Current Month"] : 0;

                    const last3Keys = [1, 2, 3].map(offset =>
                        formatYMD(subDays(today, offset))
                    );
                    const prevWeek3Keys = [1, 2, 3].map(offset =>
                        formatYMD(subDays(today, offset + 7))
                    );

                    const last3Vals = last3Keys.map(key => {
                        const o = graph.find(d => d.x === key);
                        return o ? o["Current Month"] : 0;
                    });
                    const prevWeek3Vals = prevWeek3Keys.map(key => {
                        const o = graph.find(d => d.x === key);
                        return o ? o["Current Month"] : 0;
                    });

                    const avgLast3     = last3Vals.reduce((a, b) => a + b, 0) / last3Vals.length;
                    const avgPrevWeek3 = prevWeek3Vals.reduce((a, b) => a + b, 0) / prevWeek3Vals.length;
                    const curr = data.totals.curr ?? 0;
                    const prev = data.totals.prev ?? 0;
                    const mom  = calcMoM(curr, prev);

                    const pctChange3 = avgPrevWeek3 > 0
                        ? ((avgLast3 - avgPrevWeek3) / avgPrevWeek3) * 100
                        : 0;

                    acc[placeId] = {
                        title: userCards.find(c => c.place_id === placeId)?.title || "",
                        count: formatCurrency(curr) + " ₪",
                        percentage: {
                            amount: formatPct(mom),
                            label: `vs ${data.months?.prev || "prev month"}`,
                            color: trendColor(mom),
                        },
                        previous: {
                            amount: formatCurrency(prev) + " ₪",
                            label: data.months?.prev || "Previous month",
                        },
                        currAmountRaw: curr,
                        prevAmountRaw: prev,
                        dailyComparison: {
                            amount: `${formatCurrency(yRev)} - ${formatCurrency(lwRev)} ₪`,
                            label: `yesterday vs last week`,
                        },
                        avgComparison: {
                            amount: `${pctChange3.toFixed(1)}%`,
                            label: `last 3 days avg vs previous week`,
                            color: pctChange3 >= 0 ? 'success' : 'error',
                        },
                        graphData: graph,
                    };

                    return acc;
                }, {});

                setCardStats(statsMap);

                const totalCurr = results.reduce((sum, r) => sum + r.data.totals.curr, 0);
                const totalPrev = results.reduce((sum, r) => sum + r.data.totals.prev, 0);
                const totalMoM  = calcMoM(totalCurr, totalPrev);

                setStats(s => {
                    const out = [...s];
                    out[0] = {
                        ...out[0],
                        title: `Total Revenue for last 31 days`,
                        count: formatCurrency(totalCurr) + " ₪",
                        percentage: {
                            amount: formatPct(totalMoM),
                            label: `vs ${results[0]?.data?.months?.prev || "prev month"}`,
                            color: trendColor(totalMoM),
                        },
                        previous: {
                            amount: formatCurrency(totalPrev) + " ₪",
                            label: results[0]?.data?.months?.prev || "Previous month",
                        },
                        currAmountRaw: totalCurr,
                        prevAmountRaw: totalPrev,
                    };
                    return out;
                });
            })
            .catch(console.error);
    }, [userCards]);

    // ברים ל-total
    useEffect(() => {
        if (openIdx !== 0) return setBarData([]);
        const data = userCards.map((card) => {
            const stats = cardStats[card.place_id];
            return {
                x: stats?.title || card.title || "Unknown",
                "Previous Month": stats?.prevAmountRaw || 0,
                "Current Month":  stats?.currAmountRaw || 0,
            };
        });
        setBarData(data);
    }, [openIdx, userCards, cardStats]);

    return loading ?
        <CircularProgress sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
        }}/> :
        (
            <DashboardLayout>
                <DashboardNavbar/>

                {/* אפשר להוסיף פה טקסט קטן כשמצב עריכה פעיל */}
                {isEditMode && (
                    <MDBox px={2} pb={1} color="warning.main">
                        Edit mode – click a card to edit or delete it
                    </MDBox>
                )}

                <MDBox
                    sx={{
                        display: "grid",
                        gap: 3,
                        gridTemplateColumns: { xs: "1fr", md: "1fr 3fr" },
                        minHeight: "calc(100vh - 200px)",
                    }}
                >
                    {/* LEFT COLUMN */}
                    <MDBox sx={{ display: "grid" }}>
                        <MDBox sx={{cursor: "pointer"}}>
                            <div onClick={() => !isEditMode && setOpenIdx(0)}>
                                <ComplexStatisticsCard icon={<Icon>₪</Icon>}  {...stats[0]} />
                            </div>
                            <MDBox
                                sx={{
                                    width: { xs: "100%", md: "25vw" },
                                    height: { xs: 300, md: "calc(95vh - 330px)" },
                                    marginBottom: "40%"
                                }}
                            >
                                <MapView style={{ width: "100%", height: "100%" }} />
                            </MDBox>
                        </MDBox>
                    </MDBox>

                    {/* RIGHT COLUMN – CARDS */}
                    <MDBox
                        sx={{
                            display: "grid",
                            gap: 3,
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                            gridAutoRows: "minmax(180px, auto)",
                        }}
                    >
                        {userCards.map((card, idx) => (
                            <MDBox
                                key={card.place_id}
                                sx={{
                                    cursor: "pointer",
                                    position: "relative",
                                    outline: isEditMode ? "2px dashed #facc15" : "none",
                                    outlineOffset: isEditMode ? "2px" : 0,
                                }}
                                onClick={() => {
                                    if (isEditMode) {
                                        setEditingCard(card);
                                        setEditModalOpen(true);
                                    } else {
                                        setOpenIdx(idx + 1);
                                    }
                                }}
                            >
                                <ComplexStatisticsCard
                                    title={cardStats[card.place_id]?.title}
                                    count={cardStats[card.place_id]?.count}
                                    previous={cardStats[card.place_id]?.previous}
                                    percentage={cardStats[card.place_id]?.percentage}
                                    dailyComparison={cardStats[card.place_id]?.dailyComparison}
                                    avgComparison={cardStats[card.place_id]?.avgComparison}
                                    icon={<Icon>₪</Icon>}
                                />
                            </MDBox>
                        ))}
                    </MDBox>
                </MDBox>

                <Footer/>

                {/* MODAL – total / per-card chart (כמו שהיה) */}
                <Dialog open={openIdx >= 0} onClose={() => setOpenIdx(-1)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ color: "#000" }}>
                        {openIdx === 0 && stats[0]?.title}
                        {openIdx >= 1 && (() => {
                            const cardIndex = openIdx - 1;
                            const card = userCards[cardIndex];
                            const title = cardStats[card?.place_id]?.title || "";
                            return `${title} revenues`;
                        })()}
                    </DialogTitle>
                    <DialogContent sx={{height: 400}}>
                        {openIdx === 0 && (
                            <ResponsiveBar
                                data={barData}
                                keys={["Previous Month", "Current Month"]}
                                indexBy="x"
                                margin={{top: 40, right: 130, bottom: 80, left: 60}}
                                padding={0.3}
                                groupMode="grouped"
                                colors={d => (d.id === "Current Month" ? "#e69f0f" : "#1f77b4")}
                                borderColor={{from: "color", modifiers: [["darker", 1.6]]}}
                                axisBottom={{
                                    tickRotation: -45,
                                    legend: openIdx === 0 ? "Company" : "Date",
                                    legendPosition: "middle",
                                    legendOffset: 60,
                                }}
                                axisLeft={{
                                    legend: "Income",
                                    legendPosition: "middle",
                                    legendOffset: -50,
                                }}
                                theme={{
                                    tooltip: {
                                        container: {
                                            background: '#fff',
                                            color: '#000',         // ⬅️ black text so you can read it
                                            fontSize: 12,
                                            border: '1px solid rgba(0,0,0,0.15)',
                                            borderRadius: 2,
                                            padding: '6px 9px',
                                        },
                                    },
                                }}//
                                enableLabel={false}    // ← disable in-bar labels
                                tooltip={undefined}     // ← disable hover tooltips (or supply your own)
                                tooltipLabel={({ id, data }) =>
                                    // id is either "Previous Month" or "Current Month"
                                {
                                    if (openIdx === 0) {
                                        return `${id}: ₪${data[id]?.toLocaleString()}`;
                                    }
                                    return id === "Previous Month"
                                        ? data.prevKey    // the true previous‐month date
                                        : data.x // the current‐month date
                                }}
                                legends={[
                                    {
                                        dataFrom: "keys",
                                        anchor: "bottom-right",
                                        direction: "column",
                                        translateX: 120,
                                        itemWidth: 100,
                                        itemHeight: 20,
                                        symbolSize: 12,
                                    },
                                ]}
                            />
                        ) }
                        {openIdx >= 1 && (
                            <WeekdayRevenueChart
                                transactions={
                                    (() => {
                                        const cardIndex = openIdx - 1;
                                        const card = userCards[cardIndex];
                                        const rawData = cardStats[card?.place_id]?.graphData || [];

                                        const result = [];
                                        rawData.forEach((r) => {
                                            result.push({ date: r.x, revenue: r["Current Month"] ?? 0 });
                                            if (r.prevKey) result.push({ date: r.prevKey, revenue: r["Previous Month"] ?? 0 });
                                        });
                                        return result;
                                    })()
                                }
                            />
                        )}

                    </DialogContent>
                </Dialog>

                {/* modal הוספה */}
                <AddCardModal
                    mode="add"
                    open={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    onSubmit={handleNewCard}
                    userEmail={userEmail}
                />

                {/* modal עריכה */}
                <AddCardModal
                    mode="edit"
                    open={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setEditingCard(null);
                    }}
                    onSubmit={handleEditSave}
                    onDelete={handleEditDelete}
                    userEmail={userEmail}
                    initialTitle={editingCard?.title}
                    initialPlaceId={editingCard?.place_id}
                />

                {/* FAB הוספה */}
                <IconButton
                    onClick={() => setAddModalOpen(true)}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: 'primary.main',
                        color: '#fff',
                        '&:hover': { backgroundColor: 'primary.dark' }
                    }}
                >
                    <AddIcon />
                </IconButton>

                {/* FAB עריכה */}
                <IconButton
                    onClick={toggleEditMode}
                    sx={{
                        position: 'fixed',
                        bottom: 90,
                        right: 24,
                        backgroundColor: isEditMode ? 'warning.main' : 'secondary.main',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: isEditMode ? 'warning.dark' : 'secondary.dark'
                        }
                    }}
                >
                    <EditIcon />
                </IconButton>
            </DashboardLayout>
        );
}
